import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, X, SkipForward, Timer, LogOut, Users, Zap, Trophy, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { PLAYER_COLORS, PlayerBall } from "@/components/brand";

const MAX_NAME = 16;

export default function Game() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("count"); // count | names | modes | category | question
  const [playerCount, setPlayerCount] = useState(null);
  const [names, setNames] = useState([]);
  const [modes, setModes] = useState([]); // per-player: "classique" | "expert"
  const [current, setCurrent] = useState(0);
  const [turn, setTurn] = useState(1);
  const [usedIds, setUsedIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [question, setQuestion] = useState(null);
  const [loadingQ, setLoadingQ] = useState(false);
  const [exhausted, setExhausted] = useState(null);
  const [confirm, setConfirm] = useState(null); // {message, onConfirm}

  const players = useMemo(
    () => names.map((n, i) => ({ name: n, color: PLAYER_COLORS[i % PLAYER_COLORS.length] })),
    [names]
  );
  const activePlayer = players[current];
  const activeMode = modes[current];

  const goHomeConfirm = () =>
    setConfirm({
      message: "Terminer la partie et revenir à l'accueil ?",
      confirmLabel: "Terminer",
      onConfirm: () => navigate("/"),
    });

  // ---------- Setup handlers ----------
  const chooseCount = (n) => {
    setPlayerCount(n);
    setNames(Array.from({ length: n }, (_, i) => (names[i] || "")));
    setPhase("names");
  };

  const canStartNames = names.length === playerCount && names.every((n) => n.trim().length > 0);

  const submitNames = () => {
    if (!canStartNames) return;
    setNames((prev) => prev.map((n) => n.trim().replace(/\s+/g, " ")));
    setModes(Array.from({ length: playerCount }, (_, i) => modes[i] || null));
    setPhase("modes");
  };

  const setPlayerMode = (index, m) => {
    setModes((prev) => prev.map((x, i) => (i === index ? m : x)));
  };

  const canStartGame = modes.length === playerCount && modes.every((m) => m === "classique" || m === "expert");

  const startGame = () => {
    if (!canStartGame) return;
    setCurrent(0);
    setTurn(1);
    setUsedIds([]);
    setPhase("category");
  };

  // ---------- Category / question ----------
  useEffect(() => {
    if (phase === "category") {
      api.get("/public/categories").then(({ data }) => setCategories(data)).catch(() => {});
      setExhausted(null);
    }
  }, [phase]);

  const pickCategory = useCallback(async (cat) => {
    setLoadingQ(true);
    setExhausted(null);
    try {
      const { data } = await api.post("/public/next-question", {
        category_id: cat.id,
        exclude_ids: usedIds,
        mode: activeMode,
      });
      if (data.exhausted) {
        setExhausted(cat);
        setLoadingQ(false);
        return;
      }
      setQuestion(data);
      setUsedIds((prev) => [...prev, data.id]);
      setPhase("question");
    } catch (e) {
      setExhausted(cat);
    } finally {
      setLoadingQ(false);
    }
  }, [usedIds, activeMode]);

  const nextPlayer = () => {
    setCurrent((c) => (c + 1) % players.length);
    setTurn((t) => t + 1);
    setQuestion(null);
    setPhase("category");
  };

  const sameContinue = () => {
    setTurn((t) => t + 1);
    setQuestion(null);
    setPhase("category");
  };

  const passTurn = () =>
    setConfirm({
      message: `${activePlayer?.name} passe son tour ?`,
      confirmLabel: "Passer",
      onConfirm: () => {
        setConfirm(null);
        nextPlayer();
      },
    });

  return (
    <div className="relative min-h-screen bg-dh-black dh-grain">
      <div className="pointer-events-none absolute -top-32 right-0 h-72 w-72 rounded-full bg-dh-red opacity-10 blur-[100px]" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-lg flex-col px-5 pb-8 pt-6">
        <GameHeader phase={phase} onBack={handleBack} onQuit={goHomeConfirm} turn={turn} mode={activeMode} players={players} current={current} />

        <div className="mt-6 flex-1">
          {phase === "count" && <StepCount onChoose={chooseCount} selected={playerCount} />}
          {phase === "names" && (
            <StepNames names={names} setNames={setNames} onSubmit={submitNames} canStart={canStartNames} />
          )}
          {phase === "modes" && (
            <StepModes players={players} modes={modes} onSet={setPlayerMode} onStart={startGame} canStart={canStartGame} />
          )}
          {phase === "category" && (
            <StepCategory
              player={activePlayer}
              mode={activeMode}
              categories={categories}
              onPick={pickCategory}
              onPass={passTurn}
              loading={loadingQ}
              exhausted={exhausted}
            />
          )}
          {phase === "question" && activeMode === "classique" && (
            <ClassiqueQuestion question={question} player={activePlayer} onCorrect={sameContinue} onWrong={nextPlayer} onPass={passTurn} />
          )}
          {phase === "question" && activeMode === "expert" && (
            <ExpertQuestion question={question} player={activePlayer} onGood={sameContinue} onBad={nextPlayer} onPass={passTurn} />
          )}
        </div>
      </div>

      {confirm && (
        <ConfirmSheet
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          onCancel={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
        />
      )}
    </div>
  );

  function handleBack() {
    if (phase === "names") setPhase("count");
    else if (phase === "modes") setPhase("names");
    else if (phase === "count") navigate("/");
    else goHomeConfirm();
  }
}

// ================= Header =================
function GameHeader({ phase, onBack, onQuit, turn, mode, players, current }) {
  const inGame = phase === "category" || phase === "question";
  return (
    <div className="flex items-center justify-between">
      <button data-testid="btn-back" onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-xl border border-dh-borderDark bg-dh-cardDark text-white/70 active:scale-95">
        <ArrowLeft className="h-5 w-5" />
      </button>

      {inGame ? (
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-dh-cardDark px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white/50">
            Tour {turn}
          </span>
          <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${mode === "expert" ? "bg-dh-gold/20 text-dh-gold" : "bg-dh-red/20 text-dh-red"}`}>
            {mode === "expert" ? "Expert" : "Classique"}
          </span>
        </div>
      ) : (
        <span className="font-display text-sm font-bold uppercase tracking-widest text-white/40">Nouvelle partie</span>
      )}

      {inGame ? (
        <button data-testid="btn-quit-game" onClick={onQuit} className="flex h-10 w-10 items-center justify-center rounded-xl border border-dh-borderDark bg-dh-cardDark text-white/70 active:scale-95">
          <LogOut className="h-5 w-5" />
        </button>
      ) : (
        <div className="h-10 w-10" />
      )}
    </div>
  );
}

// ================= Step: player count =================
function StepCount({ onChoose, selected }) {
  return (
    <div className="animate-fade-up">
      <StepTitle icon={<Users className="h-4 w-4" />} label="Étape 1 / 3" title="Combien de joueurs ?" />
      <div className="mt-6 grid grid-cols-3 gap-3">
        {[2, 3, 4, 5, 6].map((n) => (
          <button
            key={n}
            data-testid={`player-count-${n}`}
            onClick={() => onChoose(n)}
            className={`flex h-24 flex-col items-center justify-center rounded-2xl border text-center transition-all active:scale-95 ${
              selected === n ? "border-dh-red bg-dh-red/15" : "border-dh-borderDark bg-dh-cardDark hover:border-white/20"
            }`}
          >
            <span className="font-display text-4xl font-extrabold text-white">{n}</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-white/40">joueurs</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ================= Step: names =================
function StepNames({ names, setNames, onSubmit, canStart }) {
  const update = (i, v) => setNames((prev) => prev.map((n, idx) => (idx === i ? v.slice(0, MAX_NAME) : n)));
  return (
    <div className="animate-fade-up">
      <StepTitle icon={<Users className="h-4 w-4" />} label="Étape 2 / 3" title="Les prénoms" />
      <div className="mt-6 space-y-3">
        {names.map((n, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl border border-dh-borderDark bg-dh-cardDark p-2 pl-3 focus-within:border-dh-red">
            <PlayerBall color={PLAYER_COLORS[i % PLAYER_COLORS.length]} name={n || String(i + 1)} size={36} />
            <input
              data-testid={`input-player-name-${i + 1}`}
              value={n}
              onChange={(e) => update(i, e.target.value)}
              placeholder={`Joueur ${i + 1}`}
              maxLength={MAX_NAME}
              className="flex-1 bg-transparent py-3 text-base font-semibold text-white outline-none placeholder:text-white/30"
            />
            <span className="pr-2 text-xs text-white/30">{n.length}/{MAX_NAME}</span>
          </div>
        ))}
      </div>
      <button
        data-testid="btn-names-next"
        onClick={onSubmit}
        disabled={!canStart}
        className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-dh-red text-base font-display font-extrabold uppercase tracking-wider text-white transition disabled:opacity-30 active:scale-[0.98]"
      >
        Continuer <ArrowRight className="h-5 w-5" />
      </button>
      {!canStart && <p className="mt-3 text-center text-xs text-white/40">Chaque joueur doit avoir un prénom.</p>}
    </div>
  );
}

// ================= Step: per-player modes =================
function StepModes({ players, modes, onSet, onStart, canStart }) {
  return (
    <div className="animate-fade-up">
      <StepTitle icon={<Zap className="h-4 w-4" />} label="Étape 3 / 3" title="Le mode de chaque joueur" />
      <p className="mt-2 text-sm text-white/40">Chaque joueur choisit son mode. Il restera actif pendant toute la partie.</p>
      <div className="mt-6 space-y-4">
        {players.map((p, i) => (
          <div key={i} className="rounded-2xl border border-dh-borderDark bg-dh-cardDark p-4">
            <div className="mb-3 flex items-center gap-3">
              <PlayerBall color={p.color} name={p.name} size={36} />
              <span data-testid={`mode-player-name-${i}`} className="truncate font-display text-lg font-extrabold text-white">{p.name}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid={`btn-mode-classique-${i}`}
                onClick={() => onSet(i, "classique")}
                className={`flex flex-col items-center justify-center gap-1 rounded-xl border py-3 transition-all active:scale-[0.97] ${
                  modes[i] === "classique" ? "border-dh-red bg-dh-red/15" : "border-dh-borderDark hover:border-white/20"
                }`}
              >
                <Check className={`h-5 w-5 ${modes[i] === "classique" ? "text-dh-red" : "text-white/40"}`} />
                <span className={`font-display text-sm font-bold uppercase tracking-wide ${modes[i] === "classique" ? "text-white" : "text-white/60"}`}>Classique</span>
              </button>
              <button
                data-testid={`btn-mode-expert-${i}`}
                onClick={() => onSet(i, "expert")}
                className={`flex flex-col items-center justify-center gap-1 rounded-xl border py-3 transition-all active:scale-[0.97] ${
                  modes[i] === "expert" ? "border-dh-gold bg-dh-gold/15" : "border-dh-borderDark hover:border-white/20"
                }`}
              >
                <Timer className={`h-5 w-5 ${modes[i] === "expert" ? "text-dh-gold" : "text-white/40"}`} />
                <span className={`font-display text-sm font-bold uppercase tracking-wide ${modes[i] === "expert" ? "text-white" : "text-white/60"}`}>Expert</span>
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        data-testid="btn-start-game-final"
        onClick={onStart}
        disabled={!canStart}
        className="dh-red-glow mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-dh-red text-base font-display font-extrabold uppercase tracking-wider text-white transition disabled:opacity-30 active:scale-[0.98]"
      >
        C'est parti <ArrowRight className="h-5 w-5" />
      </button>
      {!canStart && <p className="mt-3 text-center text-xs text-white/40">Chaque joueur doit choisir un mode.</p>}
    </div>
  );
}

// ================= Step: category =================
function StepCategory({ player, mode, categories, onPick, onPass, loading, exhausted }) {
  return (
    <div className="animate-fade-up">
      <ActivePlayerBanner player={player} />
      <div className="mt-3 flex justify-center">
        <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${mode === "expert" ? "bg-dh-gold/20 text-dh-gold" : "bg-dh-red/20 text-dh-red"}`}>
          Mode {mode === "expert" ? "Expert" : "Classique"}
        </span>
      </div>
      <p className="mt-5 mb-4 text-center text-sm font-bold uppercase tracking-widest text-white/40">Choisis une catégorie</p>
      <div className="space-y-3">
        {categories.map((c) => (
          <button
            key={c.id}
            data-testid={`category-select-${c.id}`}
            onClick={() => onPick(c)}
            disabled={loading}
            className="flex w-full items-center justify-between rounded-2xl border border-dh-borderDark bg-dh-cardDark p-5 text-left transition-all hover:border-dh-red disabled:opacity-50 active:scale-[0.98]"
          >
            <span className="font-display text-xl font-bold uppercase tracking-wide text-white">{c.name}</span>
            <ArrowRight className="h-5 w-5 text-dh-red" />
          </button>
        ))}
        {categories.length === 0 && (
          <p className="rounded-2xl border border-dh-borderDark bg-dh-cardDark p-5 text-center text-sm text-white/40">Aucune catégorie disponible pour le moment.</p>
        )}
      </div>
      {exhausted && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-dh-gold/40 bg-dh-gold/10 p-4">
          <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 text-dh-gold" />
          <p className="text-sm text-white/80">
            Toutes les questions de <b>{exhausted.name}</b> ont déjà été posées dans cette partie. Choisis une autre catégorie.
          </p>
        </div>
      )}
      <button
        data-testid="btn-pass-turn-category"
        onClick={onPass}
        disabled={loading}
        className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-dh-borderDark bg-transparent text-sm font-bold uppercase tracking-wider text-white/60 disabled:opacity-40 active:scale-[0.98]"
      >
        <SkipForward className="h-4 w-4" /> Passer mon tour
      </button>
    </div>
  );
}

// ================= Classique question =================
function ClassiqueQuestion({ question, player, onCorrect, onWrong, onPass }) {
  const [selected, setSelected] = useState(null);
  const revealed = selected !== null;
  const isCorrect = revealed && selected === question.correct_index;

  const answer = (i) => {
    if (revealed) return;
    setSelected(i);
  };

  return (
    <div>
      <ActivePlayerBanner player={player} category={question.category_name} difficulty={question.difficulty} />

      <div data-testid="question-text" className="mt-5 animate-pop rounded-2xl border border-dh-borderDark bg-dh-cardDark p-5">
        <p className="text-lg font-semibold leading-relaxed text-white sm:text-xl">{question.text}</p>
      </div>

      <div className="mt-4 space-y-3">
        {question.answers.map((a, i) => {
          let cls = "border-dh-borderDark bg-dh-cardDark text-white hover:border-white/30";
          let icon = null;
          if (revealed) {
            if (i === question.correct_index) {
              cls = "border-dh-green bg-dh-green/20 text-white";
              icon = <Check className="h-5 w-5 text-dh-green" />;
            } else if (i === selected) {
              cls = "border-dh-red bg-dh-red/20 text-white animate-shake";
              icon = <X className="h-5 w-5 text-dh-red" />;
            } else {
              cls = "border-dh-borderDark bg-dh-cardDark/60 text-white/40";
            }
          }
          return (
            <button
              key={i}
              data-testid={`answer-option-${i}`}
              onClick={() => answer(i)}
              disabled={revealed}
              className={`flex min-h-[56px] w-full items-center justify-between gap-3 rounded-2xl border p-4 text-left text-base font-semibold transition-all active:scale-[0.99] ${cls}`}
            >
              <span>{a}</span>
              {icon}
            </button>
          );
        })}
      </div>

      {revealed ? (
        <ResultPanel
          correct={isCorrect}
          explanation={question.explanation}
          onContinue={isCorrect ? onCorrect : onWrong}
          continueLabel={isCorrect ? "Je continue" : "Joueur suivant"}
        />
      ) : (
        <button
          data-testid="btn-pass-turn"
          onClick={onPass}
          className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-dh-borderDark bg-transparent text-sm font-bold uppercase tracking-wider text-white/60 active:scale-[0.98]"
        >
          <SkipForward className="h-4 w-4" /> Passer mon tour
        </button>
      )}
    </div>
  );
}

// ================= Expert question =================
function ExpertQuestion({ question, player, onGood, onBad, onPass }) {
  const DURATION = 20;
  const [timeLeft, setTimeLeft] = useState(DURATION);
  const [stage, setStage] = useState("running"); // running | grading | timeout
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(intervalRef.current);
          setStage("timeout");
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const iAnswered = () => {
    clearInterval(intervalRef.current);
    setStage("grading");
  };

  const pct = timeLeft / DURATION;
  const danger = timeLeft <= 5;
  const R = 52;
  const C = 2 * Math.PI * R;

  return (
    <div>
      <ActivePlayerBanner player={player} category={question.category_name} difficulty={question.difficulty} />

      {/* Timer */}
      <div className="mt-6 flex justify-center">
        <div data-testid="expert-timer" className="relative flex h-36 w-36 items-center justify-center">
          <svg className="h-36 w-36 -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r={R} fill="none" stroke="#2A2D37" strokeWidth="10" />
            <circle
              cx="60" cy="60" r={R} fill="none"
              stroke={danger ? "#D31027" : "#F59E0B"}
              strokeWidth="10" strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C * (1 - pct)}
              style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s" }}
            />
          </svg>
          <div className={`absolute font-display text-5xl font-extrabold ${danger ? "text-dh-red animate-pop" : "text-white"}`}>
            {stage === "running" ? timeLeft : 0}
          </div>
        </div>
      </div>

      <div data-testid="question-text" className="mt-6 animate-pop rounded-2xl border border-dh-borderDark bg-dh-cardDark p-5">
        <p className="text-lg font-semibold leading-relaxed text-white sm:text-xl">{question.text}</p>
      </div>

      {stage === "running" && (
        <>
          <button
            data-testid="btn-i-answered"
            onClick={iAnswered}
            className="dh-red-glow mt-6 flex h-16 w-full items-center justify-center gap-2 rounded-2xl bg-dh-red text-lg font-display font-extrabold uppercase tracking-wider text-white active:scale-[0.98]"
          >
            J'ai répondu
          </button>
          <button
            data-testid="btn-pass-turn"
            onClick={onPass}
            className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-dh-borderDark bg-transparent text-sm font-bold uppercase tracking-wider text-white/60 active:scale-[0.98]"
          >
            <SkipForward className="h-4 w-4" /> Passer mon tour
          </button>
        </>
      )}

      {(stage === "grading" || stage === "timeout") && (
        <div className="mt-6 animate-fade-up">
          <div className="rounded-2xl border border-dh-green/40 bg-dh-green/10 p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-dh-green">Bonne réponse</p>
            <p data-testid="expert-correct-answer" className="mt-1 text-lg font-bold text-white">{question.correct_answer}</p>
            {question.explanation ? <p className="mt-2 text-sm text-white/60">{question.explanation}</p> : null}
          </div>

          {stage === "grading" ? (
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                data-testid="btn-correct-answer"
                onClick={onGood}
                className="flex h-16 flex-col items-center justify-center rounded-2xl bg-dh-green font-display text-base font-extrabold uppercase tracking-wide text-white active:scale-[0.97]"
              >
                <Check className="mb-1 h-6 w-6" /> Bonne réponse
              </button>
              <button
                data-testid="btn-wrong-answer"
                onClick={onBad}
                className="flex h-16 flex-col items-center justify-center rounded-2xl bg-dh-red font-display text-base font-extrabold uppercase tracking-wide text-white active:scale-[0.97]"
              >
                <X className="mb-1 h-6 w-6" /> Mauvaise réponse
              </button>
            </div>
          ) : (
            <div className="mt-5">
              <div className="mb-3 flex items-center justify-center gap-2 rounded-2xl border border-dh-red/40 bg-dh-red/10 p-3 text-sm font-bold uppercase tracking-wider text-dh-red">
                <Timer className="h-4 w-4" /> Temps écoulé
              </div>
              <button
                data-testid="btn-continue-timeout"
                onClick={onBad}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-dh-red font-display text-base font-extrabold uppercase tracking-wide text-white active:scale-[0.98]"
              >
                Joueur suivant <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ================= Shared bits =================
function ActivePlayerBanner({ player, category, difficulty }) {
  if (!player) return null;
  return (
    <div className="flex items-center gap-3 rounded-2xl border p-3" style={{ borderColor: player.color + "66", background: player.color + "1A" }}>
      <PlayerBall color={player.color} name={player.name} active size={44} />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Au tour de</p>
        <p data-testid="active-player-name" className="truncate font-display text-xl font-extrabold text-white">{player.name}</p>
      </div>
      {category && (
        <div className="text-right">
          <span data-testid="question-category" className="block rounded-full bg-black/40 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">{category}</span>
          {difficulty && <span className="mt-1 block text-[10px] uppercase tracking-widest text-white/40">{difficulty}</span>}
        </div>
      )}
    </div>
  );
}

function ResultPanel({ correct, explanation, onContinue, continueLabel }) {
  return (
    <div className="mt-5 animate-fade-up">
      <div className={`relative flex items-center gap-3 overflow-hidden rounded-2xl border p-4 ${correct ? "border-dh-green/40 bg-dh-green/10" : "border-dh-red/40 bg-dh-red/10"}`}>
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${correct ? "bg-dh-green/25 text-dh-green" : "bg-dh-red/25 text-dh-red"}`}>
          {correct ? <Trophy className="h-6 w-6" /> : <X className="h-6 w-6" />}
        </span>
        <div>
          <p className={`font-display text-lg font-extrabold ${correct ? "text-dh-green" : "text-dh-red"}`}>
            {correct ? "Bonne réponse !" : "Raté !"}
          </p>
          <p className="text-sm text-white/60">{correct ? "Tu rejoues, choisis une catégorie." : "La main passe au joueur suivant."}</p>
        </div>
        {correct && <Confetti />}
      </div>
      {explanation ? (
        <div className="mt-3 rounded-2xl border border-dh-borderDark bg-dh-cardDark p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Le savais-tu ?</p>
          <p className="mt-1 text-sm text-white/80">{explanation}</p>
        </div>
      ) : null}
      <button
        data-testid="btn-continue"
        onClick={onContinue}
        className={`mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl font-display text-base font-extrabold uppercase tracking-wider text-white active:scale-[0.98] ${correct ? "bg-dh-green" : "bg-dh-red"}`}
      >
        {continueLabel} <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
}

function Confetti() {
  const dots = Array.from({ length: 10 });
  return (
    <div className="pointer-events-none absolute inset-0">
      {dots.map((_, i) => (
        <span
          key={i}
          className="absolute bottom-2 h-2 w-2 rounded-sm"
          style={{
            left: `${8 + i * 9}%`,
            background: [PLAYER_COLORS[i % PLAYER_COLORS.length]],
            animation: `dh-confetti ${0.8 + (i % 4) * 0.2}s ease-out ${i * 0.04}s forwards`,
          }}
        />
      ))}
    </div>
  );
}

function StepTitle({ icon, label, title }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-dh-red">{icon}{label}</div>
      <h1 className="mt-2 font-display text-4xl font-extrabold uppercase tracking-tight text-white">{title}</h1>
    </div>
  );
}

function ConfirmSheet({ message, confirmLabel = "Confirmer", onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onCancel}>
      <div className="w-full max-w-lg animate-fade-up rounded-3xl border border-dh-borderDark bg-dh-cardDark p-5" onClick={(e) => e.stopPropagation()}>
        <p className="text-center text-base font-semibold text-white">{message}</p>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <button data-testid="confirm-cancel" onClick={onCancel} className="h-12 rounded-2xl border border-dh-borderDark text-sm font-bold uppercase tracking-wide text-white/70 active:scale-95">
            Annuler
          </button>
          <button data-testid="confirm-ok" onClick={onConfirm} className="h-12 rounded-2xl bg-dh-red text-sm font-bold uppercase tracking-wide text-white active:scale-95">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
